import { Controller } from '@hotwired/stimulus';
// Extension `.js` explicite : esbuild s'en passe, `node --test` non.
import {
  canDisplayPdfInline,
  isDesktopFirefox,
  stripPdfViewerFragment,
} from '../utils/pdf_viewer_support.js';

/**
 * Lazy Iframe Controller
 *
 * Defers setting an iframe's `src` until the controller receives a `load` action.
 * Designed to work with modals: Safari's PDF renderer computes layout at iframe
 * creation time — if the iframe is hidden (inside a modal), it renders at 0×0
 * and the PDF appears tiny. By deferring src until the modal is visible, Safari
 * computes the correct dimensions.
 *
 * No-viewer fallback (#790 extended): when the browser does not display PDFs
 * inline (`navigator.pdfViewerEnabled === false`, see `utils/pdf_viewer_support.js`),
 * loading the iframe leaves it blank AND triggers an unrequested download. With a
 * `fallback` target, `load` then leaves the iframe empty and hidden and shows the
 * fallback block (rendered by `Ui::PdfFallbackComponent`) instead. Without that
 * target the controller keeps the iframe path. Note: headless Playwright reports
 * `pdfViewerEnabled === false`, so an e2e asserting on such an iframe sees the
 * fallback.
 *
 * Targets:
 * - iframe: The `<iframe>` that displays the document
 * - fallback: (optional) block shown INSTEAD of the iframe when the browser does
 *   not display PDFs inline. Must render with `hidden`.
 * - downloadLink: (optional) `<a download>` inside the fallback, pointed at the
 *   source without its viewer fragment (`#view=FitH…`). Its `download` attribute
 *   is left as rendered: an empty value keeps the Content-Disposition filename.
 * - firefoxHint: (optional) Firefox-only settings path, revealed only on Firefox
 *
 * Values:
 * - src: The URL to load into the iframe when triggered. Empty = no document:
 *   `load` then shows neither the iframe content nor the fallback.
 * - eager: (Boolean, default false) load on `connect` instead of waiting for an
 *   action — for content that arrives after the modal opened (turbo-frame).
 *
 * Actions:
 * - load: Sets the iframe src, or shows the fallback (call when modal opens)
 * - unload: Clears the iframe src and the download link, hides the fallback
 *   (call when modal closes, frees resources)
 *
 * @example
 * <div data-controller="modal lazy-iframe"
 *      data-lazy-iframe-src-value="/path/to/file.pdf#view=FitH"
 *      data-action="modal:open->lazy-iframe#load modal:close->lazy-iframe#unload">
 *   <iframe data-lazy-iframe-target="iframe" class="w-full h-full"></iframe>
 *   <%# Ui::PdfFallbackComponent.new(controller: 'lazy-iframe') renders: %>
 *   <div class="hidden" data-lazy-iframe-target="fallback">
 *     <p class="hidden" data-lazy-iframe-target="firefoxHint">…</p>
 *     <a href="#" download="" data-lazy-iframe-target="downloadLink">…</a>
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = ['iframe', 'fallback', 'downloadLink', 'firefoxHint'];

  static values = {
    src: String,
    eager: { type: Boolean, default: false },
  };

  connect() {
    if (this.eagerValue) this.load();
  }

  load() {
    // Source first (RV-4): an empty source must show neither the iframe content
    // nor a fallback whose link would download the page itself.
    if (!this.hasIframeTarget || !this.srcValue) return;

    if (canDisplayPdfInline() || !this.hasFallbackTarget) {
      this.#toggleFallback(false);
      this.iframeTarget.src = this.srcValue;
    } else {
      this.iframeTarget.src = '';
      this.#toggleFallback(true);
      if (this.hasDownloadLinkTarget) {
        this.downloadLinkTarget.href = stripPdfViewerFragment(this.srcValue);
      }
    }
  }

  unload() {
    if (this.hasIframeTarget) {
      this.iframeTarget.src = '';
    }
    if (this.hasDownloadLinkTarget) this.downloadLinkTarget.href = '#';
    this.#toggleFallback(false);
  }

  #toggleFallback(show) {
    if (!this.hasFallbackTarget) return;

    this.fallbackTarget.classList.toggle('hidden', !show);
    if (this.hasIframeTarget) this.iframeTarget.classList.toggle('hidden', show);
    if (this.hasFirefoxHintTarget) {
      this.firefoxHintTarget.classList.toggle('hidden', !(show && isDesktopFirefox()));
    }
  }
}
